import { useEffect } from "react";
import {
    BookOpen,
    Package,
    Zap,
    AlertTriangle,
    Wrench,
    Flame,
    RefreshCw,
    ShieldCheck,
    Mail,
    ArrowUpRight,
    CheckCircle2,
} from "lucide-react";

const SUBJECT = encodeURIComponent("Request: Hard copy of Kitchensafe Installation Manual");
const BODY = encodeURIComponent(
    "Hi Tom,\n\nI'd like a hard copy of the Kitchensafe Installation Manual mailed to me.\n\nName:\nCompany:\nMailing address:\nPhone:\nEmail:\nProject details (optional):\n\nThanks,"
);

const SECTIONS = [
    {
        id: "overview",
        num: "01",
        icon: BookOpen,
        title: "System Overview",
        intro:
            "Kitchensafe USA is the exclusive distributor for Kitchensafe in the United States. The complete, comprehensive cooktop fire suppression system.",
        blocks: [
            {
                heading: "Before you begin",
                items: [
                    "When installing the Kitchensafe system, we strongly suggest you read through this entire manual.",
                    "Install a basic Kitchensafe system first. Do not connect any external items — central alarm, manual pull station, horn/strobe — until the basic system is proven out.",
                    "This eliminates any external item from interfering with the Kitchensafe system during initial setup.",
                    'Once installed, power up the system and verify the "Power" and "Appliance Supply" LED lights on the panel are illuminated.',
                ],
            },
            {
                heading: "L.E.D. Status Panel",
                items: [
                    "The L.E.D. panel allows a check of system status at a glance.",
                    "Any fault is displayed and alerts the user with an audible signal.",
                    '"Power" and "Appliance Supply" LED lights are on when in normal operating mode.',
                ],
            },
            {
                heading: "Control Box Specifications",
                items: [
                    "Compact electronic control box with many installation options.",
                    "Dimensions: 8\" × 8\" × 4\".",
                    "The front L.E.D. panel constantly displays system status.",
                    "May be mounted up to 15 feet from the nozzle to the suppressant cylinder with extended braided supply line.",
                ],
            },
        ],
    },
    {
        id: "whats-in-the-box",
        num: "02",
        icon: Package,
        title: "What's In the Box",
        intro: "Kitchensafe ships with every part needed to install a complete, ready-to-operate system.",
        blocks: [
            {
                heading: "Standard components",
                items: [
                    "Control enclosure box with front and back electronic boards",
                    "Stainless steel nozzle(s) + stainless nozzle fittings",
                    "White nylon washer",
                    "90-degree fitting",
                    "Braided suppressant supply line (steel)",
                    "Heat sensor + heat sensor wire",
                    "Electronic actuator (firing device)",
                    "Reset/test tool",
                    "Suppressant bottle (with agent)",
                    "Manual pull station (2-wire)",
                ],
            },
            {
                heading: "By fuel type",
                items: [
                    'Gas appliance: Includes a ¾" ASCO gas solenoid for automatic fuel shutoff.',
                    'Electric appliance: Includes a "shunt trip" relay box for automatic power disconnect.',
                ],
            },
            {
                heading: "First check when the box arrives",
                items: [
                    "Verify power supply location: 120-volt service. The same outlet that serves the range hood can be used.",
                    "The circuit does NOT need to be dedicated, but it DOES need to be unswitched.",
                    "IMPORTANT: Do NOT power the Kitchensafe system up until the installation is complete, with the reset tool installed.",
                ],
            },
        ],
    },
    {
        id: "mounting",
        num: "03",
        icon: Wrench,
        title: "Mounting the Control Box",
        intro:
            "The control box can be mounted inside a cabinet or directly on the wall. Up to 15 feet from the hood.",
        blocks: [
            {
                heading: "Orientation",
                items: [
                    "Grommets go on the right when mounting; the control box will be in the upright position.",
                    "The suppressant bottle must be on the right side as well — the pressure sensor and actuator wires are short.",
                ],
            },
            {
                heading: "Placement guidance",
                items: [
                    "Mark the 4 mounting holes on the back plate.",
                    "To make installation easier, you can unplug the front portion of the Kitchensafe system and remove the ground wire during mounting.",
                    "If mounted inside a cabinet, keep the bottom of the Kitchensafe unit at least 2 inches from the bottom of the cabinet — this ensures the front cover can be removed and replaced easily.",
                    "If space is limited for wiring, use the large oval hole at the back of the control box to route wires. A small wooden spacer between the control box and wall creates clearance.",
                    "If mounted inside a cabinet, drill holes for the braided supply line, appliance relay wire, and heat sensor line.",
                ],
            },
        ],
    },
    {
        id: "power-wiring",
        num: "04",
        icon: Zap,
        title: "Power & Core Wiring",
        intro: "The main electronic board has terminals for power, sensors, firing, and alarms.",
        blocks: [
            {
                heading: "Power cord",
                items: [
                    'Install the power cord into the labeled terminals: "E" = earth/ground, "N" = neutral, "L" = load.',
                ],
            },
            {
                heading: 'The "sniffer" terminal',
                items: [
                    "The sniffer terminal is used to detect any gas present on the property.",
                    "When the sniffer terminal is NOT in use, a loop must be installed in its place.",
                ],
            },
            {
                heading: "Firing pin terminal",
                items: [
                    "The firing pin terminal accepts either the test/reset tool OR the actuator/firing device — never both at once.",
                    "During servicing, the test/reset tool is placed in the firing pin terminal with the RED wire on the right side.",
                ],
            },
            {
                heading: "Reset plug",
                items: [
                    'A small green "Lego" style plug is inserted into the reset hole, located to the left of the alarm terminals.',
                ],
            },
            {
                heading: "Heat sensor wiring",
                items: [
                    "The heat sensor plug has a specific wiring order from the screw side of the plug.",
                    "If heat sensor wires must be removed from the plug to route the wire, TAKE NOTE or a PHOTO of the wire order first.",
                    "They must be reinstalled in the exact same order.",
                ],
            },
        ],
    },
    {
        id: "external-devices",
        num: "05",
        icon: AlertTriangle,
        title: "Connecting External Devices",
        intro:
            "Kitchensafe can integrate with a central alarm, manual pull station, horn/strobe, and gas/electric shutoffs.",
        blocks: [
            {
                heading: "Alarm connection (no pull station)",
                items: [
                    'If you are installing with only an alarm and NO pull station, use the "BMS" (Building Management System) terminal.',
                ],
            },
            {
                heading: "Manual pull station",
                items: [
                    "Push connectors are used for manual pull station wiring.",
                    "There is NO polarity — insert one wire into each empty hole of the two push connectors.",
                    "A 2-wire manual pull station is provided with every Kitchensafe system.",
                ],
            },
            {
                heading: "Horn/Strobe (optional)",
                items: [
                    "The system has a terminal for a horn/strobe if required.",
                    "Use one of the alarm terminals for horn/strobe installation.",
                ],
            },
            {
                heading: "Shunt trip relay (electric appliances)",
                items: [
                    'The "shunt trip" relay shuts off the power supply to the cooktop to prevent re-ignition of a fire.',
                ],
            },
            {
                heading: "Gas solenoid (gas appliances)",
                items: [
                    "Gas solenoid shut-offs are included when a gas supply is part of the kitchen system.",
                    "The Fire Marshal may require a 120-volt relay to shut off the electrical supply of a gas appliance.",
                ],
            },
            {
                heading: "Island hood installations",
                items: [
                    "Kitchensafe can be installed on island hoods.",
                    "The suppressant supply line can run up to 15 feet.",
                ],
            },
        ],
    },
    {
        id: "nozzle-install",
        num: "06",
        icon: Flame,
        title: "Nozzle Installation",
        intro: "The nozzle can be mounted on a manifold or installed directly into the hood.",
        blocks: [
            {
                heading: "Mounting options",
                items: [
                    "Nozzle manifold mounted under the hood: a small gap allows the lip of the manifold to slide under the hood.",
                    "Nozzle installed directly into the hood for a clean, flush look.",
                    "Nozzle installed into a cabinet adjacent to the cooking surface.",
                ],
            },
            {
                heading: "Distance from cooking surface",
                items: [
                    'Minimum distance from cooktop to nozzle: 16 ½"',
                    'Maximum distance from cooktop to nozzle: 33 ½"',
                ],
            },
            {
                heading: 'Larger cooktops (up to 48")',
                items: [
                    "Kitchensafe protects gas or electric cooking surfaces up to 48 inches wide.",
                    "The only change for a 48\" install is adding one additional nozzle.",
                    "The manifold then has 2 nozzles instead of 1.",
                    "Stainless fittings and braided supply line are arranged as shown in the diagram.",
                ],
            },
        ],
    },
    {
        id: "final-assembly",
        num: "07",
        icon: CheckCircle2,
        title: "Final Assembly",
        intro: "The suppressant cylinder, actuator, and final checks — in the right order.",
        blocks: [
            {
                heading: "Suppressant cylinder",
                items: [
                    "The suppressant cylinder is one of the LAST items installed.",
                    "Place the cylinder in the bottle cradle, secure with the oversize zip tie provided.",
                    "Check the pressure gauge for proper pressure.",
                    'Connect the pressure sensor wires to the "pressure sensor" terminal — no polarity.',
                ],
            },
            {
                heading: "Electronic actuator",
                items: [
                    'The electronic actuator ("squib") is the FINAL item installed into the firing pin terminal.',
                    "There is NO polarity on the actuator.",
                ],
            },
            {
                heading: "L.E.D. board / brain chip",
                items: [
                    "The back of the L.E.D. board contains a small square computer chip — the brain of the Kitchensafe system.",
                    "The chip's calendar resets to one year to signal the next inspection.",
                ],
            },
            {
                heading: "Battery back-up toggle",
                items: [
                    "The silver toggle switch is used when a battery back-up is present.",
                    "Battery back-up is NOT required and can be a source of problems if the battery is allowed to lose its charge.",
                ],
            },
            {
                heading: "In-line fuses",
                items: [
                    "There are 2 in-line fuses below the power source.",
                    "Left fuse: protects the 12-volt items.",
                    "Right fuse: protects the 120-volt items.",
                ],
            },
            {
                heading: "Internal plugs",
                items: [
                    "4-wire plug: originates on the back power board, plugs into the main board.",
                    "Flat communication cable: connects between the front and back boards.",
                    "Both plugs must be securely seated.",
                ],
            },
        ],
    },
    {
        id: "install-steps",
        num: "08",
        icon: Wrench,
        title: "Step-by-Step Installation",
        intro:
            "The complete installation sequence. Follow in order — do NOT power the system up until the last step.",
        blocks: [
            {
                heading: "Steps 1–3: Pre-install",
                items: [
                    "1. Verify all box contents against the parts list.",
                    "2. Confirm 120-volt unswitched power supply is available (same outlet as range hood is fine; does not need to be dedicated).",
                    "3. DO NOT power the Kitchensafe system up until install is complete with the reset tool installed.",
                ],
            },
            {
                heading: "Step 4: Plan the layout",
                items: [
                    "Check location for the Kitchensafe system.",
                    "Check the route for the braided supply line and the heat sensor.",
                    'Check where the cylinder will be placed — the actuator line and pressure sensor wire must be on the grommet side of the control box.',
                    "Mark the 4 mounting holes on the back plate.",
                ],
            },
            {
                heading: "Steps 5–6: Mount the back plate",
                items: [
                    "If space is limited for wiring, use the large oval hole at the back of the control box.",
                    "Place a spacer (small piece of wood) between the control box and wall for clearance.",
                    "Mount the back plate to the location (inside a cabinet or directly on the wall).",
                    "If inside a cabinet, drill holes for the braided supply line, appliance relay wire, and heat sensor line.",
                ],
            },
            {
                heading: "Step 7: Install the reset tool",
                items: [
                    "Install the reset/test tool into the firing pin terminal with the RED wire on the right side.",
                    "Insert the small green plug into the reset terminal.",
                    "DO NOT power the system up yet.",
                ],
            },
            {
                heading: "Step 8: Route the heat sensor",
                items: [
                    "Route the heat sensor wire from the center front edge of the hood to the sensor terminal on the back board of the Kitchensafe system.",
                    "Mount the heat sensor wire with a single self-tapping screw.",
                    "DO NOT drill through the hood — ensure the screw does not protrude through the hood.",
                ],
            },
            {
                heading: "Step 9: Pull station wiring (if used)",
                items: [
                    'If a pull station is to be used, install the wires into the "BMS" terminal — no polarity.',
                    "If the system is connected to a central alarm, the pull station wires go into the push-in connectors near the upper right of the back-board (one 3-wire, one 4-wire — insert pull station wires into the empty port of each).",
                ],
            },
            {
                heading: "Steps 10–12: Appliance disconnect & cylinder",
                items: [
                    "10. Route the appliance wires from the shunt relay box (electric) or gas solenoid (gas) behind the cooktop to the appliance terminal on the back board.",
                    "11. Place the 2-liter suppressant cylinder in the bottle cradle, secure with the zip tie, connect wires to the pressure sensor terminal (no polarity).",
                    "12. Verify all wires are landed properly.",
                ],
            },
            {
                heading: "Step 13: Test the system",
                items: [
                    "With the test/reset tool installed, power the Kitchensafe system on.",
                    "First, pull the pull station — you should hear an audible alert.",
                    "Remove power and reset the system.",
                    "Apply heat to the heat sensor — the elevated heat audible warning should sound. Remove heat as soon as it alerts, and allow the system to reset.",
                    "Apply heat again until the system fully activates. Reset the system following the reset instructions.",
                ],
            },
            {
                heading: "Steps 14–17: Finalize",
                items: [
                    "14. If you don't hear an audible beep when depressing the button, check the wiring — usually a wire isn't placed properly.",
                    "15. Make sure power to the Kitchensafe system is DISCONNECTED during any inspection of the wiring.",
                    '16. If the system is connected to a central alarm, the main alarm panel must be set in "TEST" mode during testing.',
                    "17. Once reset, the calendar module is set for one year. After one year, an audible alert and the \"Service Required\" LED will indicate the next inspection is due. The user can silence the alert for 24 hours at a time.",
                ],
            },
        ],
    },
    {
        id: "reset-procedure",
        num: "09",
        icon: RefreshCw,
        title: "Reset Procedure",
        intro:
            "After installation (and after any test with heat gun / pull station), the system must be formally reset.",
        blocks: [
            {
                heading: "Full reset — perform with power OFF",
                items: [
                    "1. Place the test tool leads into the firing pin terminal — RED wire on the right.",
                    "2. Place the small green plug into the reset plug.",
                    "3. Turn ON main power to the system.",
                    '4. Confirm that the "Power" and "Appliance" L.E.D. lights are illuminated.',
                    "5. Depress the button on the test tool for 1 second. Listen for the audible beep.",
                    "6. Turn OFF the power to the Kitchensafe system.",
                    "7. Remove the reset tool from the firing pin terminal, and remove the small green plug from the reset terminal.",
                    '8. Place the actuator/squib into the firing pin terminal — no polarity.',
                    "9. Power the system back up.",
                    '10. Confirm only the "Power" and "Appliance Supply" L.E.D.s are illuminated.',
                ],
            },
            {
                heading: "After reset",
                items: [
                    "The system is now reset and a one-year timer has been placed on the internal clock.",
                    "At the one-year mark, the system will audibly alert the user that service is due.",
                    'Press the "Silence Alarm" button on the front panel to silence the alert for 24 hours.',
                ],
            },
        ],
    },
    {
        id: "troubleshooting",
        num: "10",
        icon: AlertTriangle,
        title: "Troubleshooting",
        intro: "If something isn't right, 95% of the time it's a wire that isn't landed properly.",
        blocks: [
            {
                heading: "First checks (power off)",
                items: [
                    "Check all wiring connections — all should be solid and tight.",
                    "Trace from the power source and beyond.",
                    "Check the small in-line fuses — both should be good.",
                    "If there is no power to the appliance, unplug the relay box from the wall and confirm the wires to the appliance terminals and relay box are properly attached.",
                    "Check that the ribbon wire is securely installed at each plug end on the front and back electronic boards.",
                    "Check that the green 4-wire power plug is secure.",
                ],
            },
            {
                heading: "Physical checks",
                items: [
                    "Confirm the system control box is securely mounted.",
                    "Check for water or dirt ingress.",
                    "Check that cables are routed correctly, not pinched, and not damaged inside or outside the system box.",
                    "The ribbon wire is easily caught when closing the front lid — be careful.",
                    "Damaged cables MUST be replaced.",
                    "Check for signs of extinguishing agent leakage.",
                    "Check for corrosion due to contact with suppressant agent.",
                    "Check that all hoses are in good condition and routed without kinks or twists.",
                    "Check that the nozzle manifold (if used) is securely mounted.",
                    "Check that the temperature sensor is secure and mounted at the centerline of the hood.",
                ],
            },
            {
                heading: 'L.E.D. test — "SENSOR TEMP HIGH" (elevated heat alert)',
                items: [
                    "Remove the firing device, place the test tool in its place to prevent firing the metron.",
                    "Power on the system and allow it to initialize (Power + Appliance Supply lights on).",
                    'Using a HOT AIR GUN ONLY, slowly raise the temperature at the sensor until the "SENSOR TEMP HIGH" L.E.D. illuminates.',
                    "Audible alert should sound. Remove heat immediately.",
                    'Allow sensor to cool and watch for "SENSOR TEMP HIGH" to go out.',
                    "DO NOT use open flame — the sensor can be damaged.",
                ],
            },
            {
                heading: 'L.E.D. test — "FIRE DETECTED" (full activation)',
                items: [
                    "Turn a single gas or electric burner on the cooktop.",
                    'Using a hot air gun, apply heat to the temperature sensor. "SENSOR TEMP HIGH" L.E.D. illuminates first; audible alert sounds.',
                    'Continue heating until the "FIRE DETECTED" L.E.D. illuminates — then STOP heating.',
                    "When FIRE DETECTED illuminates, the L.E.D. on the test tool should also illuminate.",
                    "The oven should shut off when the system activates.",
                    "At this point, the gas solenoid or shunt trip relay should shut off the gas or electric supply to the cooktop.",
                ],
            },
            {
                heading: "After testing",
                items: [
                    "Turn OFF the power to the Kitchensafe system.",
                    "Follow the full reset procedure.",
                    "After reset, turn the power back on and replace the firing pin / squib.",
                    "Confirm the gas or electric cooktop is turned off at the burner control.",
                    'When cooled, turn the system on and confirm the "Power" and "Appliance" L.E.D.s are illuminated.',
                    "VERY IMPORTANT: If you used the pull station during testing, make sure it is properly closed and secured — otherwise the system will activate.",
                ],
            },
            {
                heading: "L.E.D. illumination test",
                items: [
                    'Hold the "L.E.D. TEST" button on the front of the Kitchensafe control box.',
                    "Confirm all L.E.D.s illuminate.",
                ],
            },
        ],
    },
    {
        id: "summary",
        num: "11",
        icon: ShieldCheck,
        title: "System Summary",
        intro: "The complete Kitchensafe feature list — one page, one system, no missing modules.",
        blocks: [
            {
                heading: "Standard features",
                items: [
                    "Small electronic control enclosure with many mounting options",
                    "Front L.E.D. panel constantly displays system status",
                    "Installs on any residential hood (even island hoods)",
                    'Protects cooktops up to 48" wide',
                    "Fuel source shutoff for gas and electric appliances",
                    "Low suppressant cylinder warning",
                    "Audible elevated heat signal (avoids unwanted discharge)",
                    "Connects to central alarm system",
                    "Kitchensafe may be installed up to 15 feet from the cooktop",
                    "System self-monitors 24/7 and warns of any issue",
                    "Horn/Strobe ready",
                    'Rapid-fire "knock down" — about 5 seconds',
                    "Suppressant agent cleans up with soap and water",
                    "Approved to UL300A and the most recent NFPA fire-extinguishing system standards",
                    'Self-contained — delivered with shunt trip relay, pull station, alarm relay, and test tool. "No extra modules to purchase."',
                ],
            },
        ],
    },
];

function TableOfContents() {
    return (
        <div className="border border-white/10 bg-[#141416] p-6 md:p-8">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935] mb-5">
                In this manual
            </div>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
                {SECTIONS.map((s) => (
                    <li key={s.id}>
                        <a
                            href={`#${s.id}`}
                            data-testid={`toc-${s.id}`}
                            className="group flex items-baseline gap-3 text-sm hover:text-[#E53935] text-[#a1a1aa] transition-colors"
                        >
                            <span className="font-mono-tech text-[11px] text-[#71717a] group-hover:text-[#E53935] tabular-nums">
                                {s.num}
                            </span>
                            <span className="font-display font-medium">{s.title}</span>
                        </a>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function SectionBlock({ block }) {
    return (
        <div className="border-l-2 border-white/10 pl-6 md:pl-8 py-2 mb-6 last:mb-0 hover:border-[#E53935]/50 transition-colors">
            <h4 className="font-display font-bold text-base md:text-lg text-white mb-3">
                {block.heading}
            </h4>
            <ul className="space-y-2.5">
                {block.items.map((item, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-3 text-sm md:text-base text-[#a1a1aa] leading-relaxed"
                    >
                        <span className="font-mono-tech text-[11px] text-[#71717a] mt-1.5 tabular-nums flex-shrink-0">
                            —
                        </span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Section({ section }) {
    const Icon = section.icon;
    return (
        <section
            id={section.id}
            data-testid={`section-${section.id}`}
            className="scroll-mt-24 py-12 md:py-16 border-t border-white/10"
        >
            <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                    <div className="sticky top-28">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-12 h-12 flex items-center justify-center border border-white/10 bg-[#141416]">
                                <Icon className="w-5 h-5 text-[#E53935]" strokeWidth={1.75} />
                            </div>
                            <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#71717a]">
                                Part {section.num}
                            </span>
                        </div>
                        <h3 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight leading-tight mb-4">
                            {section.title}
                        </h3>
                        <p className="text-sm md:text-base text-[#a1a1aa] leading-relaxed">
                            {section.intro}
                        </p>
                    </div>
                </div>
                <div className="lg:col-span-8">
                    {section.blocks.map((block, i) => (
                        <SectionBlock key={i} block={block} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function HardCopyRequest() {
    return (
        <section
            data-testid="hard-copy-request"
            className="relative py-20 md:py-24 border-t border-white/10 overflow-hidden"
        >
            <div className="absolute top-0 left-0 right-0 h-1 bg-[#E53935]" />

            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-2 h-2 bg-[#E53935] rounded-full pulse-dot" />
                            <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                                Prefer paper?
                            </span>
                        </div>
                        <h2 className="font-display font-extrabold text-3xl md:text-5xl text-white leading-[1.0] tracking-tight mb-6">
                            Would you like a{" "}
                            <span className="text-[#E53935] italic font-medium">hard copy</span> of
                            this manual?
                        </h2>
                        <p className="text-base md:text-lg text-[#a1a1aa] leading-relaxed max-w-xl">
                            Send us a message and we'll mail one out to you. Printed, bound, and
                            ready for the install site — no screen required.
                        </p>
                    </div>

                    <div className="lg:col-span-5">
                        <a
                            href={`mailto:tom@kitchensafe.net?subject=${SUBJECT}&body=${BODY}`}
                            data-testid="hard-copy-cta"
                            className="group block border border-white/10 bg-[#141416] hover:bg-[#1a1a1e] p-6 md:p-8 transition-colors"
                        >
                            <div className="flex items-center gap-4 mb-5">
                                <div className="w-12 h-12 flex items-center justify-center bg-[#E53935] flex-shrink-0">
                                    <Mail className="w-5 h-5 text-white" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#71717a] mb-1">
                                        Request a mailed copy
                                    </div>
                                    <div className="font-display font-bold text-xl text-white tracking-tight">
                                        Email Tom
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-[#a1a1aa] leading-relaxed mb-5">
                                Opens a pre-filled email. Just add your name, mailing address, and
                                phone number, then hit send.
                            </p>
                            <div className="flex items-center gap-2 text-[#E53935] group-hover:gap-3 transition-all font-display font-bold text-sm">
                                Send request
                                <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </a>

                        <a
                            href="tel:4806945234"
                            data-testid="hard-copy-call"
                            className="mt-3 block text-center font-mono-tech text-xs uppercase tracking-[0.22em] text-[#71717a] hover:text-white transition-colors p-3"
                        >
                            Or call Tom · 480-694-5234
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function InstallationManual() {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        document.title = "Installation Manual | Kitchensafe USA";
    }, []);

    return (
        <main data-testid="installation-manual-page">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 border-b border-white/10 bg-grid-fine">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                            Technical Documentation
                        </span>
                    </div>
                    <h1 className="font-display font-extrabold text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-[-0.03em] mb-8">
                        Installation
                        <br />
                        <span className="text-[#E53935] italic font-medium">Manual</span>.
                    </h1>
                    <p className="text-base md:text-lg text-[#a1a1aa] leading-relaxed max-w-2xl mb-10">
                        Everything you need to install a Kitchensafe exhaust hood fire suppression
                        system — from unboxing to the final reset. Read through once before you
                        begin. For first-time installers, call Tom and he'll walk you through it.
                    </p>
                    <TableOfContents />
                </div>
            </section>

            {/* All sections */}
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                {SECTIONS.map((s) => (
                    <Section key={s.id} section={s} />
                ))}
            </div>

            <HardCopyRequest />
        </main>
    );
}
